import {
  Box,
  Checkbox,
  ClickAwayListener,
  Grid,
  IconButton,
  PaginationItem,
  Typography,
  useMediaQuery,
} from '@mui/material';
import MuiPagination from '@mui/material/Pagination';
import { GridPagination, useGridApiRef } from '@mui/x-data-grid-pro';
import React, {
  memo,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import { Link, useParams } from 'react-router-dom';

import {
  getIssuesActionMenu,
  confirmationInitial,
} from '../../../../packages/common/api';
import WarningModal from '../../../../packages/common/shared-ui/src/components/WarningModal';
import CheckboxIcon from '../../../../packages/common/shared-ui/src/icons/CheckboxIcon';
import CheckboxIconChecked from '../../../../packages/common/shared-ui/src/icons/CheckboxIconChecked';
import ChevronIcon from '../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import DownloadButton from '../../../../packages/common/shared-ui/src/icons/DownloadButton';
import PaginationNextButtonIcon from '../../../../packages/common/shared-ui/src/icons/PaginationNextButtonIcon';
import PaginationPreviousButtonIcon from '../../../../packages/common/shared-ui/src/icons/PaginationPreviousButtonIcon';
import ParentIssueDropdown from '../../../../packages/common/shared-ui/src/icons/ParentIssueDropdown';
import SortIcon from '../../../../packages/common/shared-ui/src/icons/SortIcon';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';
import { store } from '../../app/redux';
import SpentTimeToolbar from '../IssuesToolbar';
import TableSecondStepPopover from '../TableSecondStepPopover';
import { UpdateIssueDto } from '../api/dto/update-issue.dto';
import {
  useDeleteIssueBulkMutation,
  useDeleteIssueMutation,
  useUpdateIssuesBulkMutation,
} from '../api/issues.api';
import { IssuesAPIResponse } from '../api/types';

import {
  StyledContextMenuItem,
  StyledContextPopover,
  StyledDataGridTableRoot,
} from './styles';

type Props = {
  readonly loading: boolean;
  issuesRowsData: IssuesAPIResponse['rows'];
  issuesColumnsData: IssuesAPIResponse['fields'];
  contextMenuData: unknown;
  pagination: {
    page: number;
    total: number;
    pageSize: number;
  };
  setContextMenuData: (data) => void;
  onPageChange: (page: number) => void;
  changeSorting: (sortModel) => void;
};

const IssuesTable: React.FC<Props> = props => {
  const {
    loading,
    issuesRowsData,
    issuesColumnsData,
    contextMenuData,
    pagination,
    onPageChange,
    changeSorting,
    setContextMenuData,
  } = props;

  const [updateIssue, { isLoading: isIssueUpdating }] =
    useUpdateIssuesBulkMutation();
  const [deleteIssue, { isLoading: isIssueDeleting }] =
    useDeleteIssueMutation();
  const [deleteIssueBulk, { isLoading: isIssueDeletingBulk }] =
    useDeleteIssueBulkMutation();

  const getLocalString = useTranslation();
  const apiRef = useGridApiRef();
  const navigate = useNavigate();
  const state = store.getState();
  const [blockConfirm, setBlockConfirm] = useState(false);
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('lg'));
  const [tableSelectedValues, setTableSelectedValues] = useState([]);
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const [sortModel, setSortModel] = useState([]);
  const [, forceUpdate] = useReducer(x => x - 1, 0);
  const [showDeleteIssueWarning, setShowDeleteIssueWarning] = useState(false);
  const [issuesRows, setIssuesRows] = useState([]);
  const [issuesColumns, setIssuesColumns] = useState([]);
  const pageParams = useParams();
  const [openWarningModal, setOpenWarningModal] = useState(false);

  const handleGetRedirectUrl = useCallback(
    ({ id, columnType }) => {
      const issueLink = pageParams.projectId
        ? `/projects/${pageParams.projectId}/issues/${id}`
        : `/issues/${id}`;
      switch (columnType) {
        case 'project':
          return `${window.location.origin}/projects/${id}`;
        case 'issue':
        case 'issue.subject':
        case 'id':
          return issueLink;
        default:
          return '';
      }
    },
    [pageParams.projectId],
  );

  const cellRenderer = useCallback(
    ({ props, column }) => {
      const linkFields = ['project', 'issue', 'issueId'];
      if (!props.value && props.row.groupName) {
        if (props.field === 'issue.issue_id') {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'right',
                width: '100%',
              }}
            >
              <Typography
                variant='h4'
                sx={{
                  marginRight: '10px',
                  fontWeight: 700,
                }}
              >
                {props.row.groupName}
              </Typography>
              {props.row.groupData}
            </Box>
          );
        }
        return null;
      } else {
        if (
          linkFields.includes(column.fieldType) ||
          column.fieldId === 'issue.subject'
        ) {
          if (column.fieldType === 'issueId') {
            props.value = props.id + '$$' + props.id + '$$issue';
          }
          const splittedColumnName = props?.value?.split('$$');
          return (
            <Box
              sx={{
                wordBreak: 'break-word',
                display: 'flex',
                justifyContent:
                  column.fieldType === 'issueId' ? 'right' : 'left',
                padding:
                  column.fieldId === 'issue.subject' && props.row.level !== 0
                    ? `0 0 0 ${
                        props.row.hasSubtasks && props.row.level > 0
                          ? `${props.row.level * 16}px!important`
                          : !props.row.hasSubtasks && props.row.level > 0
                            ? `${props.row.level * 16 + 16}px!important`
                            : 0
                      }`
                    : 0,
              }}
            >
              {column.fieldId === 'issue.subject' && props.row.hasSubtasks && (
                <ParentIssueDropdown></ParentIssueDropdown>
              )}
              <Link
                to={handleGetRedirectUrl({
                  id:
                    column.fieldType === 'project'
                      ? props.row.projectId
                      : splittedColumnName[1],
                  columnType:
                    column.fieldType === 'project'
                      ? 'project'
                      : splittedColumnName[2],
                })}
              >
                {splittedColumnName?.[0]}
              </Link>
            </Box>
          );
        }
      }
    },
    [handleGetRedirectUrl],
  );

  useEffect(() => {
    if (!issuesRowsData) return;

    const transformedRows = issuesRowsData
      .map(issue => {
        if (issue.rowType === 'normal') {
          const transformedColumns = issue.columns.reduce(
            (columnAcc, column) => {
              const fieldType =
                column.fieldId === 'issue.subject'
                  ? 'issue.subject'
                  : column.fieldType;
              switch (column.fieldId) {
                case 'issue.issue':
                case 'issue.parentIssue':
                  return {
                    ...columnAcc,
                    [column.fieldId]: `${column.value}$$${column.valueId}$$${fieldType}`,
                  };
                case 'issue.subject':
                  return {
                    ...columnAcc,
                    [column.fieldId]: `${column.value}$$${issue.rowId}$$${fieldType}`,
                  };
                case 'issue.trackerId':
                case 'issue.projectId':
                  return {
                    ...columnAcc,
                    [column.fieldId]: column.value,
                    [column.fieldId + 'Id']: column.valueId,
                  };
                default:
                  return {
                    ...columnAcc,
                    [column.fieldId]: column.value,
                  };
              }
            },
            {},
          );

          return {
            id: issue.rowId,
            ...transformedColumns,
            level: issue.services.level,
            hasSubtasks: issue.services.hasSubtasks,
            projectId: issue.services.projectId,
          };
        }

        if (issue.rowType === 'group') {
          const columnsData = issue.columns
            .filter(column => column.value !== undefined)
            .map(column => `${column.fieldName}: ${column.value}`)
            .join(' ');

          const emptyRow =
            issuesColumnsData.reduce((obj, col) => {
              obj[col.fieldId] = null;
              return obj;
            }, {}) || {};

          return {
            id: issue.rowId,
            groupName: issue.rowName,
            groupData: columnsData,
            ...emptyRow,
            isGroup: true,
          };
        }

        return null;
      })
      .filter(Boolean);

    setIssuesRows(transformedRows);
  }, [issuesRowsData, issuesColumnsData]);

  useEffect(() => {
    if (issuesColumnsData) {
      const issueIdField = {
        instance: 'issue',
        field: 'id',
        fieldId: 'issue.issue_id',
        fieldName: 'Id',
        fieldType: 'issueId',
        issuesColumns: true,
        isChecked: true,
        isCustom: false,
        canSort: true,
        position: -1,
      };
      const smallFields = ['date', 'hours', 'float', 'bool', 'issueId'];
      const newColumns = [issueIdField].concat(issuesColumnsData);
      const data = newColumns.map(column => {
        const headerName = column.fieldName;
        //let headerNameWidth = (headerName.length / 2) * 10 + 60;
        let padding = 25;
        if (headerName.length <= 6) {
          padding = 40;
        }
        const headerWidth = headerName.length * 7 + padding;
        const headerNameWidth =
          headerName.length > 15 ? 100 : headerName.length * 7 + padding;
        const columnWidth = 'auto';
        const columnFlex = 1;
        let columnType = '';
        switch (column.fieldType) {
          case 'issueId':
          case 'bool':
            columnType = 'number';
            //columnFlex = 0;
            break;
          case 'issue_status':
          case 'project':
          case 'issue':
          case 'list':
            columnType = 'string';
            break;
          case 'date':
          case 'hours':
          case 'float':
            columnType = 'Date';
            break;
          default:
            columnType = 'string';
            break;
        }
        return {
          field: column.fieldId,
          headerName: headerName,
          renderHeader: props => (
            <Typography
              variant='h4'
              sx={{
                fontWeight: 700,
              }}
            >
              {props.colDef.headerName}
            </Typography>
          ),
          disableExport: false,
          position: column.position,
          sortable: column.canSort,
          //flex: columnFlex,
          width: headerWidth,
          minWidth: headerNameWidth,
          //minWidth: headerNameWidth * 1.2,
          //width: headerNameWidth,
          maxWidth: 500,
          renderCell: props => cellRenderer({ props, column }),
          type: columnType,
          cellClassName: props => {
            const cellClasses = [];
            if (!props.row.groupName) {
              cellClasses.push('tableCell');
            }
            if (props.row.groupName) {
              cellClasses.push('tableCell tableGroupCell');
            }
            if (column.fieldId === 'issue.subject') {
              switch (props.row.level) {
                case 1:
                  cellClasses.push('tableCelllevelOne');
                  break;
                case 2:
                  cellClasses.push('tableCelllevelTwo');
                  break;
                case 3:
                  cellClasses.push('tableCelllevelThree');
                  break;
                default:
                  break;
              }
            }
            return cellClasses;
          },
        };
      });
      setIssuesColumns(data);
    }
  }, [cellRenderer, issuesColumnsData]);

  const contextPopperStylePosition = useRef({
    left: 0,
    top: 0,
    maxWidth: 180,
  });
  const handleCopyIssue = useCallback(() => {
    const tracker = contextMenuData.currentRow['issue.trackerIdId'];
    const project = contextMenuData.currentRow['projectId'];
    let url = `/issues/${contextMenuData.currentRow.id}/copy?trackerId=${tracker}&projectId=${project}&list`;
    if (contextMenuData.currentRows?.length > 1) {
      const issuesIds = contextMenuData.currentRows.join('&ids%5B%5D=');
      url = url + 'bulk_edit?copy=1&ids%5B%5D=' + issuesIds;
    }
    if (pageParams.projectId) {
      url = `/projects/${pageParams.projectId}/issues/${contextMenuData.currentRow.id}/copy?trackerId=${tracker}&projectId=${project}&list`;
    }
    //url = generateRedirectUrl({ host: window.location.origin, path: url });
    window.open(url, '_blank');
  }, [contextMenuData, pageParams.projectId]);
  const handleEditIssue = useCallback(() => {
    let url = '';
    url = `/issues/${contextMenuData.currentRow.id}/edit?list`;
    if (pageParams.projectId) {
      url = `/projects/${pageParams.projectId}/issues/${contextMenuData.currentRow.id}/edit?list`;
    }
    if (contextMenuData.currentRows?.length > 1) {
      const issuesIds = contextMenuData.currentRows.join(',');
      url = `/issues/bulk_edit?issues=${issuesIds}&list`;
      if (pageParams.projectId) {
        url = `/projects/${pageParams.projectId}/issues/bulk_edit?issues=${issuesIds}&list`;
      }
    }
    //url = generateRedirectUrl({ host: window.location.origin, path: url });
    window.open(url, '_blank');
  }, [contextMenuData, pageParams.projectId]);

  const handleDeleteIssue = useCallback(async () => {
    try {
      let response;
      if (contextMenuData.currentRows?.length > 1) {
        response = await deleteIssueBulk(
          contextMenuData.currentRows.map(item => Number(item)),
        );
      } else {
        response = await deleteIssue(contextMenuData.currentRow.id);
      }
      if (response) {
        setOpenWarningModal(false);
      }
    } catch (error) {
      error.response.status === 420 && setShowDeleteIssueWarning(true);
      console.error('ERROR WITH DELETE', error);
    }
  }, [contextMenuData.currentRow.id, contextMenuData.currentRows]);
  const handleInitConfirmation = useCallback(async () => {
    try {
      setBlockConfirm(true);
      let rows = [];
      contextMenuData.currentRow &&
        rows.push(Number(contextMenuData.currentRow.id));
      contextMenuData.currentRows.length > 0 &&
        (rows = contextMenuData.currentRows.map(item => Number(item)));
      const { data: response } = await confirmationInitial(rows);
      if (response) {
        setBlockConfirm(false);
        navigate(`/confirmations/${Number(response)}`);
      }
    } catch (error) {
      console.error('ERROR WITH POST CONFIRMATION INITIAL', error);
      setBlockConfirm(false);
    }
  }, [contextMenuData.currentRow, contextMenuData.currentRows, navigate]);

  const handleReinitConfirmation = useCallback(async () => {
    let issuesIds = [];
    contextMenuData.currentRow &&
      issuesIds.push(Number(contextMenuData.currentRow.id));
    contextMenuData.currentRows.length > 0 &&
      (issuesIds = contextMenuData.currentRows.join(','));
    navigate(`/confirmation/reinit?issues=${issuesIds}`);
  }, [contextMenuData, navigate]);

  const handleChooseSubItemType = useCallback(
    (type, value, itemPosition) => {
      const itemsCount = tableSelectedValues.length === 1 ? 5 : 4;
      const valueLength = value.length;
      setContextMenuData(prevState => {
        return {
          ...prevState,
          isSecondStepVisible: !prevState.isSecondStepVisible,
          secondPopoverStepValue: value,
          secondStepType: type,
          positionInfo: {
            itemPosition: itemPosition,
            positionDelta: itemsCount - itemPosition - valueLength,
            totalCount: valueLength,
          },
        };
      });
    },
    [setContextMenuData, tableSelectedValues.length],
  );

  const handleChooseSubItem = useCallback(
    async (value, type) => {
      setIsContextMenuOpen(false);
      const updateIssuesQuery: UpdateIssueDto = {
        ids: tableSelectedValues.map(item => Number(item)),
        issue: {},
      };
      if (type === 'assigned') {
        setContextMenuData(prevState => {
          const newAssignedTo = prevState.data.assignedTo.map(item => {
            item.isChecked = false;
            if (item.valueId === value) {
              item.isChecked = true;
            }
            return item;
          });
          prevState.data.assignedTo = newAssignedTo;
          return { ...prevState, isSecondStepVisible: false };
        });
        updateIssuesQuery.issue.assignedTo = value;
      } else if (type === 'status') {
        setContextMenuData(prevState => {
          const newStatuses = prevState.data.statuses.map(item => {
            item.isChecked = false;
            if (item.valueId === value) {
              item.isChecked = true;
            }
            return item;
          });
          prevState.data.statuses = newStatuses;
          return { ...prevState, isSecondStepVisible: false };
        });
        updateIssuesQuery.issue.statusId = value;
      } else if (type === 'priority') {
        setContextMenuData(prevState => {
          const newStatuses = prevState.data.priorities.map(item => {
            item.isChecked = false;
            if (item.valueId === value) {
              item.isChecked = true;
            }
            return item;
          });
          prevState.data.priorities = newStatuses;
          return { ...prevState, isSecondStepVisible: false };
        });
        updateIssuesQuery.issue.priority = value;
      }

      updateIssue(updateIssuesQuery);
    },
    [setContextMenuData, tableSelectedValues],
  );

  const handleUnselect = useCallback(() => {
    setIsContextMenuOpen(false);
    setContextMenuData(prevState => ({
      ...prevState,
      isSecondStepVisible: false,
      currentRow: {},
    }));
    setTableSelectedValues([]);
  }, [setContextMenuData]);

  const { current: dataGridComponents } = useRef({
    ColumnUnsortedIcon: () => (
      <SortIcon width='20' height='20' viewBox='0 0 20 20' />
    ),
    ColumnSortedAscendingIcon: () => (
      <SortIcon direction='up' width='20' height='20' viewBox='0 0 20 20' />
    ),
    ColumnSortedDescendingIcon: () => (
      <SortIcon direction='down' width='20' height='20' viewBox='0 0 20 20' />
    ),
    ExportIcon: () => {
      return (
        <IconButton disableRipple disabled>
          <DownloadButton width='24' height='24' viewBox='0 0 24 24' />
        </IconButton>
      );
    },
    Toolbar: SpentTimeToolbar,
    BaseCheckbox: props => {
      return (
        <Checkbox
          {...props}
          disableRipple
          icon={<CheckboxIcon width='12' height='12' viewBox='0 0 12 12' />}
          checkedIcon={
            <CheckboxIconChecked width='12' height='12' viewBox='0 0 12 12' />
          }
          indeterminateIcon={
            <CheckboxIcon width='12' height='12' viewBox='0 0 12 12' />
          }
        />
      );
    },
  });

  const selectRow = useCallback(
    ({ rowId, clear = false }) => {
      setTableSelectedValues(prevState => {
        if (clear) {
          return [rowId];
        }
        if (prevState.includes(rowId)) {
          return prevState.filter(id => id !== rowId);
        }
        return [...prevState, rowId];
      });
      forceUpdate();
    },
    [setTableSelectedValues],
  );

  const handleRowContextMenu = useCallback(
    async e => {
      e.preventDefault();
      if (!e.currentTarget) {
        return;
      }
      const groupRow = e.currentTarget.className.includes('groupRow');
      const rowId = e.currentTarget.getAttribute('data-id');
      const selectedRows =
        tableSelectedValues.length > 0 ? tableSelectedValues.join('||') : rowId;
      const contexIssueMenuData = await getIssuesActionMenu(selectedRows);
      if (!isContextMenuOpen && !groupRow) {
        const currentRow = issuesRows.find(row => row.id === rowId);
        setContextMenuData(prevState => {
          prevState.currentRow = currentRow;
          prevState.currentRows = tableSelectedValues;
          prevState.isSecondStepVisible = false;
          prevState.secondStepType = '';
          prevState.data = contexIssueMenuData.data;
          return prevState;
        });
        contextPopperStylePosition.current.left = e.clientX;
        contextPopperStylePosition.current.top = e.clientY;
        if (!tableSelectedValues.includes(rowId)) {
          selectRow({ rowId: rowId });
        }
        setIsContextMenuOpen(true);
        forceUpdate();
      }
    },
    [
      tableSelectedValues,
      issuesRows,
      isContextMenuOpen,
      setContextMenuData,
      selectRow,
    ],
  );

  const handleModalAction = useCallback(
    async e => {
      switch (e.currentTarget.attributes.value.value) {
        case 'save':
          await handleDeleteIssue();
          break;
        case 'delete':
          setOpenWarningModal(false);
          setShowDeleteIssueWarning(false);
          break;
      }
    },
    [handleDeleteIssue],
  );

  const PaginationComponent = useCallback(() => {
    const totalPages = Math.ceil(pagination.total / pagination.pageSize);
    return (
      <MuiPagination
        color='primary'
        count={totalPages}
        siblingCount={1}
        boundaryCount={0}
        page={pagination.page + 1}
        renderItem={item => {
          if (item.type === 'next') {
            return (
              !item.disabled && (
                <Box>
                  {getLocalString('labels', 'next')}
                  <PaginationItem
                    {...item}
                    slots={{
                      next: PaginationNextButtonIcon,
                    }}
                  />
                </Box>
              )
            );
          } else if (item.type === 'previous') {
            return (
              !item.disabled && (
                <Box>
                  <PaginationItem
                    {...item}
                    slots={{
                      previous: PaginationPreviousButtonIcon,
                    }}
                  ></PaginationItem>
                  {getLocalString('labels', 'previous')}
                </Box>
              )
            );
          } else {
            return <PaginationItem {...item}></PaginationItem>;
          }
        }}
        onChange={(event, page) => {
          onPageChange(page - 1);
        }}
      />
    );
  }, [
    pagination.total,
    pagination.pageSize,
    pagination.page,
    onPageChange,
    getLocalString,
  ]);

  const CustomPagination = useCallback(() => {
    return (
      <GridPagination
        labelDisplayedRows={() => ''}
        rowsPerPageOptions={[]}
        ActionsComponent={PaginationComponent}
      />
    );
  }, [PaginationComponent]);
  const handleSortModelChange = useCallback(
    newSortModel => {
      const validSortModel = newSortModel.filter(
        item =>
          item &&
          typeof item.field === 'string' &&
          ['asc', 'desc'].includes(item.sort),
      );

      setSortModel(validSortModel);
      changeSorting(validSortModel);
    },
    [changeSorting],
  );

  React.useEffect(() => {
    if (apiRef.current && apiRef.current.autosizeColumns) {
      apiRef.current.autosizeColumns({
        includeOutliers: true,
        includeHeaders: true,
        expand: true,
      });
    }
  }, [issuesColumns, issuesRows]);

  const handleWarningModalClose = useCallback(() => {
    setOpenWarningModal(true);
  }, []);

  return (
    <>
      {isSmallScreen ? (
        <></>
      ) : (
        <>
          <WarningModal
            issue
            simple
            isWarningModalOpen={showDeleteIssueWarning}
            handleToggle={handleModalAction}
            closeWarningModal={() => setShowDeleteIssueWarning(false)}
            title={getLocalString('messages', 'deleteIssue')}
          />
          <WarningModal
            issue
            simple
            isWarningModalOpen={openWarningModal}
            handleToggle={handleModalAction}
            closeWarningModal={() => setOpenWarningModal(false)}
            title={getLocalString('messages', 'confirmDelete')}
          />
          {isContextMenuOpen && (
            <ClickAwayListener onClickAway={handleUnselect}>
              <StyledContextPopover
                keepMounted
                open={isContextMenuOpen}
                style={contextPopperStylePosition.current}
                anchorPosition={contextPopperStylePosition.current}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                anchorReference='none'
              >
                <TableSecondStepPopover
                  value={contextMenuData.secondPopoverStepValue}
                  type={contextMenuData.secondStepType}
                  data={contextMenuData}
                  handleChooseSubItem={handleChooseSubItem}
                  isSecondStepVisible={contextMenuData.isSecondStepVisible}
                  contextPopperStylePosition={contextPopperStylePosition}
                  positionInfo={contextMenuData.positionInfo}
                />
                <Grid container direction='column'>
                  <StyledContextMenuItem
                    disabledStyle={!contextMenuData.data.canEdit}
                    item
                    xs={12}
                    onClick={handleEditIssue}
                    sx={{
                      padding: '6px 20px',
                    }}
                  >
                    <Typography variant='h5'>
                      {getLocalString('labels', 'edit')}
                    </Typography>
                  </StyledContextMenuItem>
                  <StyledContextMenuItem
                    disabledStyle={!contextMenuData.data.canEdit}
                    container
                    wrap='nowrap'
                    onClick={() =>
                      handleChooseSubItemType(
                        'status',
                        contextMenuData.data.statuses,
                        1,
                      )
                    }
                    sx={{
                      justifyContent: 'space-between',
                      padding: '6px 0 6px 20px',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant='h5'>
                      {getLocalString('labels', 'status')}
                    </Typography>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                      }}
                    >
                      <ChevronIcon
                        direction='right'
                        color='rgba(228, 228, 239, 1)'
                        viewBox='0 0 24 24'
                      />
                    </Box>
                  </StyledContextMenuItem>
                  <StyledContextMenuItem
                    disabledStyle={!contextMenuData.data.canEdit}
                    container
                    justifyContent='space-between'
                    padding='6px 0 6px 20px'
                    wrap='nowrap'
                    alignItems='center'
                    onClick={() =>
                      handleChooseSubItemType(
                        'priority',
                        contextMenuData.data.priorities,
                        1,
                      )
                    }
                  >
                    <Typography variant='h5'>
                      {getLocalString('labels', 'priority')}
                    </Typography>
                    <Box width={24} height={24}>
                      <ChevronIcon
                        direction='right'
                        color='rgba(228, 228, 239, 1)'
                        viewBox='0 0 24 24'
                      />
                    </Box>
                  </StyledContextMenuItem>
                  {contextMenuData.data.canInitConfirmation && (
                    <StyledContextMenuItem
                      item
                      xs={12}
                      disabled={blockConfirm}
                      onClick={handleInitConfirmation}
                      sx={{
                        padding: '6px 20px',
                      }}
                    >
                      <Typography variant='h5'>
                        {getLocalString('labels', 'init')}
                      </Typography>
                    </StyledContextMenuItem>
                  )}
                  {contextMenuData.data.canReinitConfirmation && (
                    <StyledContextMenuItem
                      item
                      xs={12}
                      onClick={handleReinitConfirmation}
                      sx={{
                        padding: '6px 20px',
                      }}
                    >
                      <Typography variant='h5'>
                        {getLocalString('labels', 'reinit')}
                      </Typography>
                    </StyledContextMenuItem>
                  )}
                  <StyledContextMenuItem
                    disabledStyle={!contextMenuData.data.canEdit}
                    container
                    wrap='nowrap'
                    onClick={() =>
                      handleChooseSubItemType(
                        'assigned',
                        contextMenuData.data.assignedTo,
                        2,
                      )
                    }
                    sx={{
                      justifyContent: 'space-between',
                      padding: '6px 0 6px 20px',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant='h5'>
                      {getLocalString('labels', 'assigned')}
                    </Typography>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                      }}
                    >
                      <ChevronIcon
                        direction='right'
                        color='rgba(228, 228, 239, 1)'
                        viewBox='0 0 24 24'
                      />
                    </Box>
                  </StyledContextMenuItem>
                  {tableSelectedValues.length === 1 && (
                    <StyledContextMenuItem
                      disabledStyle={!contextMenuData.data.canCopy}
                      item
                      xs={12}
                      onClick={() => handleCopyIssue()}
                      sx={{
                        padding: '6px 20px',
                      }}
                    >
                      <Typography variant='h5'>
                        {getLocalString('labels', 'copy')}
                      </Typography>
                    </StyledContextMenuItem>
                  )}
                  <StyledContextMenuItem
                    disabledStyle={!contextMenuData.data.canDelete}
                    item
                    xs={12}
                    onClick={() => handleWarningModalClose()}
                    sx={{
                      padding: '6px 20px',
                    }}
                  >
                    <Typography variant='h5'>
                      {getLocalString('labels', 'delete')}
                    </Typography>
                  </StyledContextMenuItem>
                </Grid>
              </StyledContextPopover>
            </ClickAwayListener>
          )}
          <Grid
            container
            sx={{
              flexDirection: 'column',
              flex: 1,
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <StyledDataGridTableRoot
              localeText={{
                noRowsLabel: getLocalString('labels', 'noData'),
                footerRowSelected: count =>
                  count !== 1
                    ? `${count.toLocaleString()} ${getLocalString('labels', 'selectedRows')}`
                    : `${count.toLocaleString()} ${getLocalString('labels', 'selectedRow')}`,
              }}
              apiRef={apiRef}
              pagination
              loading={
                loading ||
                isIssueUpdating ||
                isIssueDeleting ||
                isIssueDeletingBulk
              }
              sx={{
                '& .MuiTablePagination-spacer': {
                  display: 'none',
                },
                [`& .MuiDataGrid-columnHeader`]: {
                  maxHeight: 'none !important',
                  minHeight: '75px !important',
                  height: 'auto !important',
                  whiteSpace: 'inherit !important',
                  overflow: 'inherit !important',
                  lineHeight: '24px !important',
                },
                [`& .MuiDataGrid-columnHeaderTitle`]: {
                  whiteSpace: 'normal !important',
                  minHeight: '75px !important',
                },
                [`& .MuiDataGrid-columnHeaderTitleContainer`]: {
                  whiteSpace: 'normal !important',
                  minHeight: '75px !important',
                },
              }}
              columnHeaderHeight={70}
              checkboxSelection
              disableColumnFilter
              disableRowSelectionOnClick
              disableColumnMenu
              columns={issuesColumns}
              rows={issuesRows}
              rowSelectionModel={tableSelectedValues}
              onRowSelectionModelChange={newRowSelectionModel => {
                setTableSelectedValues([...newRowSelectionModel]);
              }}
              getRowId={item => item.id}
              sortModel={Array.isArray(sortModel) ? sortModel : []}
              onSortModelChange={handleSortModelChange}
              sortingMode='server'
              paginationMode='server'
              paginationModel={{
                page: pagination.page,
                pageSize: pagination.pageSize || 100,
              }}
              onPaginationModelChange={newModel => {
                onPageChange(newModel.page);
              }}
              rowCount={Number(pagination.total) || 0}
              slotProps={{
                row: {
                  onContextMenu: e => handleRowContextMenu(e),
                  style: { cursor: 'context-menu' },
                },
                baseCheckbox: {
                  icon: (
                    <CheckboxIcon width='12' height='12' viewBox='0 0 12 12' />
                  ),
                  checkedIcon: (
                    <CheckboxIconChecked
                      width='12'
                      height='12'
                      viewBox='0 0 12 12'
                    />
                  ),
                },
              }}
              slots={{
                pagination: CustomPagination,
                columnUnsortedIcon: () => (
                  <SortIcon width='20' height='20' viewBox='0 0 20 20' />
                ),
                columnSortedAscendingIcon: () => (
                  <SortIcon
                    direction='up'
                    width='20'
                    height='20'
                    viewBox='0 0 20 20'
                  />
                ),
                columnSortedDescendingIcon: () => (
                  <SortIcon
                    direction='down'
                    width='20'
                    height='20'
                    viewBox='0 0 20 20'
                  />
                ),
              }}
              getRowClassName={params => params.row.groupName && 'groupRow'}
              isRowSelectable={params => !params.row.groupName}
              getRowHeight={() => 'auto'}
            />
          </Grid>
        </>
      )}
    </>
  );
};

export default memo(IssuesTable);
